import { TextOrNumber } from "../../../features/ui"
import { Currency, TableName } from "../../../tables/types/types"
import { FiscalDocumentItem, FiscalDocumentStatus } from "../../fiscal-documents"
import { PaymentStatus, ItemPriceBreakdownType } from "../../operations"
import { IdentifierType } from "../../system"

export type SettingsDataType = TextOrNumber | object

export enum SettingsModule {
    PURCHASES = 'Purchases',
    SALES = 'Sales'
}

export type Settings = {
    companyId: string
    id: string
    module: SettingsModule
    [prop: string]: SettingsDataType
}

export enum DocumentKey {
    BUDGET = 'budget',
    SALE = 'sale',
    HOARD = 'hoard',
    HOARD_STATUS = 'hoardStatus',
    PAYMENT_RECEIPT = 'paymentReceipt',
    DELIVERY_NOTE = 'deliveryNote'
}

export type DocumentFooters = {
    [key: string]: string
}

export type DocumentPaymentTypeIdsData = {
    [customerTypeId: string]: {
        latePaymentOnly: boolean
        paymentTypeIds: string[]
    }
}

export type DocumentPaymentTypeIds = {
    [key: string]: DocumentPaymentTypeIdsData
}

export type CategorySaleYield = {
    categoryId: string
    saleYield: number
}

export enum TaxType {
    SALE_TAX = 'SaleTax',
    VAT = 'VAT',
    PERCEPTION = 'Perception',
    RETENTION = 'Retention'
}

export enum TaxBase {
    PRICE_WITHOUT_VAT = 'Price without VAT',
    VAT = 'VAT',
    PRICE_WITH_VAT = 'Price with VAT'
}

export enum VatTaxCode {
    VAT_21 = 'IVA21',
    VAT_10_5 = 'IVA10,5'
}

export enum Period {
    DAILY = 'Daily',
    WEEKLY = 'Weekly',
    MONTHLY = 'Monthly',
    ANNUAL = 'Annual',
    PER_OPERATION = 'PerOperation'
}

export enum VatCondition {
    REGISTERED_TAXPAYER = 'VAT Registered Taxpayer',
    EXEMPT = 'VAT Exempt',
    FINAL_CONSUMER = 'Final Consumer',
    SIMPLIFIED_TAXPAYER = 'Simplified Taxpayer',
    NON_CATEGORIZED = 'Non Categorized',
    FOREIGN_SUPPLIER = 'Foreign Supplier',
    FOREIGN_CLIENT = 'Foreign Client',
    RELEASED = 'VAT Released',
    SOCIAL_SIMPLIFIED_TAXPAYER = 'Social Simplified Taxpayer',
    NOT_APPLICABLE = 'Not Applicable',
    PROMOTED_INDEPENDENT_SIMPLIFIED_TAXPAYER = 'Promoted Independent Simplified Taxpayer'
}

export type Tax = {
    companyId: string
    id: string
    code: string
    legalCode?: string
    name: string
    type: TaxType
    taxBase: TaxBase
    nonTaxableMin: number
    nonTaxableMinPeriod?: Period
    yield: number
    archived?: boolean
    system?: boolean
    negativeStateUserId?: string
    negativeStateAt?: string
}

export type TaxItem = {
    taxCode: string
    taxYield: number
    amount: number
}

export enum CashFundType {
    CASH = 'Cash',
    BANCK_ACCOUNT = 'Bank Account'
}

export type CashFund = {
    companyId: string
    id: string
    name: string
    type: CashFundType
    availableAmount: number
    reservedAmount: number
    currency: Currency
    archived?: boolean
    negativeStateUserId?: string
    negativeStateAt?: string
}

export enum CheckType {
    PHYSICAL = 'Physical',
    E_CHEQ = 'E-Cheq'
}

export enum CheckStatus {
    EMITTED = 'Emitted',
    DEFERRED = 'Deferred',
    REJECTED = 'Rejected',
    ANNULLED = 'Annulled',
    ACTIVE_PENDING = 'ActivePending',
    DISOWNED = 'Disowned',
    PAYED = 'Payed',
    ENDORSED = 'Endorsed'
}

export type Check = {
    companyId: string
    id: string
    type: CheckType
    name: string
    amount: number
    issueDate: string
    paymentDate: string
    entryDate: string
    taxId: number
    endorsable: boolean
    status: CheckStatus
    archived?: boolean
    negativeStateUserId?: string
    negativeStateAt?: string
}

export type CreditCard = {
    companyId: string
    id: string
    type: string
    issuer: string
    cardholder: string
    last4Numbers: number
    dueDate: string
    archived?: boolean
    negativeStateUserId?: string
    negativeStateAt?: string
}

export enum TransactionSourceType {
    MANUAL_ADDITION = 'ManualAddition',
    CASH_FUND = 'CashFund',
    CHECK = 'Check',
    CREDIT_CARD = 'CreditCard',
    CREDIT_PAYMENT = 'CreditPayment',
    SALE = 'Sale',
    HOARD = 'Hoard',
    CANCELLATION = 'Cancellation'
}

export enum TransactionDestinationType {
    MANUAL_SUBSTRACTION = 'ManualSubstraction',
    CASH_FUND = 'CashFund',
    CHECK = 'Check',
    CREDIT_IN_FAVOR = 'CreditInFavor',
    FINANCIAL_COST = 'FinancialCost',
    ROUNDING = 'Rounding',
    PAYMENT_ORDER = 'PaymentOrder'
}

export type Transaction = {
    companyId: string
    id: string
    date: string
    sourceType: TransactionSourceType
    sourceId?: string
    amount: number
    destinationType: TransactionDestinationType
    destinationId?: string
    currency: Currency
    concept?: string
}

export enum PaymentCategory {
    CASH_FUND = 'CashFund',
    CHECK = 'Check',
    CARD = 'Card',
    CREDIT_ACCOUNT = 'CreditAccount'
}

export type PaymentType = {
    companyId: string
    id: string
    name: string
    category: PaymentCategory
    yield: number
    financialCost: number
    accreditationDays: number
    cashFundIds: string[]
    roundingEnabled?: boolean
    maxRoundingAmount?: number
    // Deshabilitado: no se ofrece para elegir (ej. una promo de cuotas fuera de su período), pero se puede volver a habilitar
    disabled?: boolean
    archived?: boolean
    installments?: number
    negativeStateUserId?: string
    negativeStateAt?: string
}

export enum Area {
    ADMINISTRATION = 'Administration',
    LOGISTICS = 'Logistics',
    MANAGMENT = 'Managment',
    PURCHASES = 'Purchases',
    SALES = 'Sales',
    WAREHOUSE = 'Warehouse',
    HR = 'Human Resources'
}

export type Employee = {
    companyId: string
    id: string
    name: string
    identifier?: number
    area: Area
    email: string
    phone?: number
    address?: string
    branchId: string
    archived?: boolean
    negativeStateUserId?: string
    negativeStateAt?: string
}

export type Expense = {
    companyId: string
    id: string
    code: number
    name: string
    expenseTypeId: string
    date: string
    branchId: string
    amount: number
    currency: Currency
    detail?: string
    employeeId: string
    supplierId?: string
    refundEmployeeId?: string
    paymentStatus: PaymentStatus
    archived?: boolean
    negativeStateUserId?: string
    negativeStateAt?: string
}

export type ExpenseType = {
    companyId: string
    id: string
    name: string
    archived?: boolean
    negativeStateUserId?: string
    negativeStateAt?: string
}

export enum TicketType {
    TICKET_A = "TicketA",
    TICKET_B = "TicketB",
    TICKET_C = "TicketC",
    RECEIPT_X = "ReceiptX",
    CREDIT_NOTE = 'CreditNote',
    SALARY = 'Salary'
}

export type Ticket = {
    companyId: string
    id: string
    code: number
    parentTable: TableName
    parentId: string
    type: TicketType
    creationDate: string
    dueDate: string
    taxItems: TaxItem[]
    subtotal: number
    vatAmount: number
    total: number
    payedAmount: number
    currency: Currency
    ticketUrl?: string
}

export enum PaymentOrderStatus {
    EMITTED = 'Emitted',
    PAYED = 'Payed'
}

export type PaymentItem = {
    paymentTypeId: string
    paymentCategory: PaymentCategory
    sourceId: string
    amount: number
    paymentDate: string
}

export type PaymentTicketItem = {
    ticketId: string
    payedAmount: number
}

export type PaymentOrder = {
    companyId: string
    id: string
    code: number
    ticketItems: PaymentTicketItem[]
    paymentItems: PaymentItem[]
    taxItems: TaxItem[]
    payedAmount: number
    status: PaymentOrderStatus
    creationDate: string
    currency: Currency
    receiptUrl?: string
    archived?: boolean
    negativeStateUserId?: string
    negativeStateAt?: string
}

export type InvoiceData = {
    emissionDate: Date
    companyLegalName: string,
    companyTaxId: number,
    companyAddress: string,
    customerLegalName: string,
    customerIdentifierType: IdentifierType
    customerIdentifier: number
    customerPhone?: number
    customerEmail?: string
    customerVatCondition: VatCondition
    items: ItemPriceBreakdownType[],
    paymentType: string,
    subtotal: number,
    vatAmount: number,
    total: number
}

export type CreditPaymentOperationItem = {
    table: TableName
    operationId: string
    amount: number
}

export type CreditPayment = {
    companyId: string
    id: string
    code: number
    paymentTypeId?: string
    destinationId?: string
    amount: number
    customerId: string
    operationItems: CreditPaymentOperationItem[]
    status: PaymentOrderStatus
    creationDate: string
    initialDebt: number
    initialCredit?: number
    currency: Currency
    receiptUrl?: string
    archived?: boolean
    negativeStateUserId?: string
    negativeStateAt?: string
}

export enum AdjustmentNoteType {
    CREDIT = 'Credit',
    DEBIT = 'Debit'
}

// What originated the note. The server sets it on the notes it creates; older notes don't have it (the manual ones are
// recognized by `manual` or by the name of their items)
export enum AdjustmentNoteKind {
    // Manual price adjustment (not from cancelled or returned items): if created before invoicing, the invoice discounts it
    MANUAL = 'Manual',
    // Fiscal note of a price corrected in a financial hoard prices list: it credits the invoiced units, but the balance
    // was already given back by the corrected price, so it doesn't adjust the hoard
    PRICE_CORRECTION = 'PriceCorrection'
}

export type AdjustmentNoteItem = FiscalDocumentItem & { itemId?: string }

export type AdjustmentNote = {
    companyId: string
    id: string
    code: number
    type: AdjustmentNoteType
    table: TableName
    operationId: string
    items: AdjustmentNoteItem[]
    amount: number
    currency: Currency
    date: string
    // Why the note was created (manual credit notes). Internal reference only: not shown in the PDF
    reason?: string
    kind?: AdjustmentNoteKind
    // Manual notes created before `kind`
    manual?: boolean
    fiscalStatus: FiscalDocumentStatus
    fiscalDocumentId?: string
}
